import React, { useEffect, useState, useRef } from "react";
import { Upload, PowerOff, Power, X, Leaf, ClipboardCheck, Play, CheckCircle2, XCircle, RotateCcw, Eye, Undo2, Send, AlertTriangle, Rocket, ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { buildSelectionCsv, downloadCsv } from "@/lib/tableSelection";
import { toast } from "sonner";
import { ScrollableTable } from "@/components/admin/ScrollableTable";

const STATUS = {
  ready: "bg-[#ECF3EC] text-[#2E7D46] border-[#CBE3D1]",
  loading: "bg-amber-50 text-amber-700 border-amber-200",
  disabled: "bg-slate-100 text-slate-500 border-slate-200",
};

const REVIEW_STATUS = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  in_review: "bg-[#EEF3FF] text-[#3457A6] border-[#CBD8F2]",
  review_complete: "bg-[#ECF3EC] text-[#2E7D46] border-[#CBE3D1]",
};

const DECISION_STYLE = {
  accepted: "bg-[#ECF3EC] text-[#2E7D46] border-[#CBE3D1]",
  rejected: "bg-rose-50 text-rose-600 border-rose-200",
  requires_review: "bg-amber-50 text-amber-700 border-amber-200",
};

function ImportDialog({ onClose, onReviewCreated }) {
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);

  const analyze = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tourism-data/admin/imports/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const createReview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tourism-data/admin/reviews", { upload_id: analysis.upload_id });
      toast.success(`Revue créée : ${data.counts.total_facts} faits normalisés`);
      onReviewCreated(data.review_id);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="tourism-import-dialog">
      <div className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-[#17201A]">Importer une source touristique (revue)</h3>
          <button onClick={onClose} data-testid="tourism-close-btn"><X size={18} /></button>
        </div>

        {!analysis ? (
          <div className="space-y-4">
            <input type="file" accept=".csv,.xlsx" data-testid="tourism-file-input" onChange={(e) => setFile(e.target.files[0])} className="block w-full text-sm" />
            <button onClick={analyze} disabled={!file || busy} data-testid="tourism-analyze-btn" className="rounded-lg bg-[#1E3D2B] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
              {busy ? "Analyse…" : "Analyser"}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-[#F4F7F4] px-4 py-3 text-sm text-slate-600" data-testid="tourism-analysis-summary">
              <p><span className="font-semibold text-[#1E3D2B]">{analysis.filename}</span> — {analysis.row_count} lignes, {analysis.columns?.length} colonnes.</p>
              <p className="mt-1 text-xs text-slate-500">La normalisation et la détection sémantique sont effectuées par le pipeline adaptatif. Aucune publication n&apos;a lieu : une revue est créée pour validation Platform Admin.</p>
            </div>
            {analysis.warnings?.length > 0 && (
              <ul className="list-disc space-y-1 pl-5 text-xs text-amber-700" data-testid="tourism-analysis-warnings">
                {analysis.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
            <button onClick={createReview} disabled={busy} data-testid="tourism-create-review-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#1E3D2B] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
              <ClipboardCheck size={15} /> {busy ? "Création…" : "Créer une revue"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Counts({ c }) {
  if (!c) return null;
  return (
    <div className="flex flex-wrap gap-2 text-[11px] font-semibold" data-testid="review-counts">
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">Total {c.total_facts}</span>
      <span className="rounded-full bg-[#ECF3EC] px-2 py-0.5 text-[#2E7D46]">Acceptés {c.accepted}</span>
      <span className="rounded-full bg-rose-50 px-2 py-0.5 text-rose-600">Rejetés {c.rejected}</span>
      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">À revoir {c.requires_review}</span>
      <span className="rounded-full bg-orange-50 px-2 py-0.5 text-orange-700">Bloqués {c.blocked}</span>
      <span className="rounded-full bg-purple-50 px-2 py-0.5 text-purple-700">Conflits {c.conflict_count}</span>
    </div>
  );
}

function ReviewDetail({ reviewId, onClose, onChanged, onPrev, onNext, hasPrev, hasNext }) {
  const [review, setReview] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState({});
  const [preview, setPreview] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const publishingRef = useRef(false);
  const [selected, setSelected] = useState(() => new Set());
  const [selectAllFilter, setSelectAllFilter] = useState(false);
  const [page, setPage] = useState(0);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { decision, scope, count, label }
  const PAGE_SIZE = 50;

  const load = async () => {
    try { const { data } = await api.get(`/tourism-data/admin/reviews/${reviewId}`); setReview(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  // Reset per-review UI state on every review change: preview (never reuse another
  // review's counters), selection, pagination and filter.
  useEffect(() => {
    setReview(null); setPreview(null); setSelected(new Set());
    setSelectAllFilter(false); setPage(0); setFilter("all");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);

  // Escape closes the innermost confirmation first, then the review itself.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (bulkConfirm) { setBulkConfirm(null); return; }
      if (confirmPublish) { setConfirmPublish(false); return; }
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, bulkConfirm, confirmPublish]);

  const transition = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tourism-data/admin/reviews/${reviewId}/${action}`, { revision: review.revision });
      setReview(data); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const decide = async (fid, decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tourism-data/admin/reviews/${reviewId}/facts/${fid}/decision`,
        { decision, review_note: notes[fid] || null, revision: review.revision });
      setReview(data); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const loadPreview = async () => {
    try { const { data } = await api.get(`/tourism-data/admin/reviews/${reviewId}/preview`); setPreview(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const doPublish = async () => {
    if (publishingRef.current) return;    // synchronous double-click / re-entry guard
    publishingRef.current = true;
    setPublishing(true);
    try {
      // CONTROL-ONLY payload — never any numeric/metric/period value from the client.
      // Idempotent on the server: a timeout+retry re-uses the same publication_id.
      const { data } = await api.post(`/tourism-data/admin/reviews/${reviewId}/publish`,
        { expected_revision: review.revision });
      toast.success(data.already_published || data.idempotent
        ? `Déjà publié : ${data.row_count} faits (publication ${data.publication_id})`
        : `Publié dans Lodge Reference Data : ${data.row_count} faits`);
      setConfirmPublish(false);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPublishing(false); publishingRef.current = false; }
  };

  const exportSelectionCsv = () => {
    const rows = (selectAllFilter ? facts : (review.facts || []).filter((f) => selected.has(f.fact_identity)));
    const columns = [
      { label: "Métrique", get: (f) => f.metric_code },
      { label: "Période", get: (f) => `${f.period?.period_type || ""} ${f.period?.period_start || ""}${f.period?.period_end && f.period.period_end !== f.period.period_start ? " → " + f.period.period_end : ""}`.trim() },
      { label: "Valeur", get: (f) => f.value },
      { label: "Unité", get: (f) => f.unit || "" },
      { label: "Confiance", get: (f) => f.mapping_confidence || "" },
      { label: "Décision", get: (f) => f.decision },
    ];
    downloadCsv(buildSelectionCsv(rows, columns), `revue_${reviewId}_selection.csv`);
    toast.success(`${rows.length} ligne(s) exportée(s)`);
  };

  const bulkApply = async ({ decision, scope }) => {
    setBusy(true);
    try {
      const payload = { decision, scope, filter, revision: review.revision };
      if (scope === "selection") payload.fact_identities = Array.from(selected);
      const { data } = await api.post(`/tourism-data/admin/reviews/${reviewId}/facts/bulk-decision`, payload);
      setReview(data);
      setSelected(new Set()); setSelectAllFilter(false); setBulkConfirm(null);
      onChanged && onChanged();
      toast.success(`Décision groupée appliquée : ${data.bulk_result?.changed ?? 0} fait(s) mis à jour`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!review) return null;
  const facts = (review.facts || []).filter((f) => {
    if (filter === "all") return true;
    if (filter === "conflicted") return f.conflict;
    return f.decision === filter;
  });
  const canDecide = review.status === "in_review";
  const pageCount = Math.max(1, Math.ceil(facts.length / PAGE_SIZE));
  const curPage = Math.min(page, pageCount - 1);
  const pageFacts = facts.slice(curPage * PAGE_SIZE, curPage * PAGE_SIZE + PAGE_SIZE);
  const pageIds = pageFacts.map((f) => f.fact_identity);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleRow = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); setSelectAllFilter(false); return n; });
  const togglePage = () => setSelected((s) => {
    const n = new Set(s);
    if (allPageSelected) pageIds.forEach((id) => n.delete(id)); else pageIds.forEach((id) => n.add(id));
    setSelectAllFilter(false); return n;
  });
  const selCount = selectAllFilter ? facts.length : selected.size;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="review-detail-dialog"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={`Revue ${review.source?.filename || ""}`}
    >
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur">
          <div>
            <h3 className="font-heading text-lg font-bold text-[#17201A] break-all" data-testid="review-title">Revue — {review.source?.filename}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${REVIEW_STATUS[review.status] || STATUS.disabled}`} data-testid="review-status">{review.status}</span>
              {review.geo?.geo_name && <span>Destination : <b className="text-[#1E3D2B]">{review.geo.geo_name}</b></span>}
              {review.geo?.geo_level && <span>Niveau : {review.geo.geo_level}</span>}
              {review.geo?.country_code && <span>Pays : {review.geo.country_code}</span>}
              {review.publisher && <span>Éditeur : {review.publisher}</span>}
              {review.period?.period_type && <span data-testid="review-period">Période : {review.period.period_type} {review.period.period_start}{review.period.period_end && review.period.period_end !== review.period.period_start ? ` → ${review.period.period_end}` : ""}</span>}
              {review.tables?.total_tables != null && <span>Tables : {review.tables.total_tables}</span>}
              <span>rev {review.revision}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button onClick={onPrev} disabled={!hasPrev} aria-label="Revue précédente" title="Revue précédente" data-testid="review-prev-btn" className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"><ChevronLeft size={14} /> Précédente</button>
            <button onClick={onNext} disabled={!hasNext} aria-label="Revue suivante" title="Revue suivante" data-testid="review-next-btn" className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40">Suivante <ChevronRight size={14} /></button>
            <button onClick={onClose} aria-label="Fermer la revue" title="Fermer" data-testid="review-close-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2C5540]"><X size={15} /> Fermer</button>
          </div>
        </div>

        <div className="p-6">
        <div className="mb-3"><Counts c={review.counts} /></div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          {review.status === "draft" && (
            <button onClick={() => transition("start")} disabled={busy} data-testid="review-start-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Play size={14} /> Démarrer la revue</button>
          )}
          {review.status === "in_review" && (
            <button onClick={() => transition("complete")} disabled={busy} data-testid="review-complete-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><CheckCircle2 size={14} /> Terminer la revue</button>
          )}
          {review.status === "review_complete" && (
            <>
              <button onClick={() => transition("reopen")} disabled={busy || publishing} data-testid="review-reopen-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"><Undo2 size={14} /> Rouvrir</button>
              <button
                onClick={() => setConfirmPublish(true)}
                disabled={busy || publishing || (review.counts?.accepted || 0) === 0 || review.published}
                title={(review.counts?.accepted || 0) === 0 ? "Aucun fait accepté éligible" : "Publier vers Lodge Reference Data"}
                data-testid="review-publish-btn"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                <Send size={14} /> Publish to Lodge Reference Data
              </button>
            </>
          )}
          <button onClick={loadPreview} data-testid="review-preview-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"><Eye size={14} /> Aperçu (non publiant)</button>
        </div>

        {review.status === "review_complete" && (
          <div className="mb-4 rounded-lg border border-[#CBE3D1] bg-[#F4F9F5] px-4 py-3 text-sm" data-testid="publish-candidate-summary">
            <p className="font-semibold text-[#1E3D2B]">Candidats à la publication</p>
            <p className="mt-1 text-xs text-slate-600">
              Total {review.counts?.total_facts} · <b className="text-[#2E7D46]">Publiables (acceptés) {review.counts?.accepted}</b> ·
              Rejetés {review.counts?.rejected} · À revoir {review.counts?.requires_review} ·
              Bloqués {review.counts?.blocked} · Conflits {review.counts?.conflict_count}
            </p>
            <p className="mt-1 text-[11px] text-slate-500">Seuls les faits <b>acceptés</b> seront publiés. Les faits rejetés, à revoir et en conflit sont exclus.</p>
          </div>
        )}

        {review.published && review.publication && (
          <div className="mb-4 rounded-lg border border-[#CBE3D1] bg-[#ECF3EC] px-4 py-3 text-sm" data-testid="publication-banner">
            <p className="flex items-center gap-1.5 font-semibold text-[#2E7D46]"><Rocket size={14} /> Publié dans Lodge Reference Data</p>
            <p className="mt-1 text-xs text-slate-600" data-testid="publication-id">
              Publication : <span className="font-mono">{review.publication.publication_id}</span> · {review.publication.row_count} faits · rev {review.publication.revision}
            </p>
          </div>
        )}

        {preview && (
          <div className="mb-4 rounded-lg border border-[#CBD8F2] bg-[#EEF3FF] px-4 py-3 text-sm" data-testid="review-preview-panel">
            <p className="font-semibold text-[#3457A6]">Aperçu non publiant — aucun fait n&apos;est écrit dans Lodge Reference Data.</p>
            <p className="mt-1 text-xs text-slate-600">Candidats acceptés : {preview.accepted_count} · Rejetés : {preview.rejected_count} · À revoir : {preview.requires_review_count} · Bloqués : {preview.blocked_count} · Conflits : {preview.conflict_count}</p>
          </div>
        )}

        <div className="mb-2 flex flex-wrap gap-2 text-xs">
          {["all", "requires_review", "accepted", "rejected", "conflicted"].map((f) => (
            <button key={f} onClick={() => { setFilter(f); setPage(0); setSelectAllFilter(false); }} data-testid={`review-filter-${f}`} className={`rounded-full border px-3 py-1 font-medium ${filter === f ? "border-[#1E3D2B] bg-[#1E3D2B] text-white" : "border-slate-200 text-slate-500"}`}>{f}</button>
          ))}
        </div>

        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs" data-testid="bulk-action-bar">
            <span className="font-semibold text-slate-600" data-testid="bulk-selected-count">{selCount} sélectionné(s){selectAllFilter ? " (tout le filtre)" : ""}</span>
            {selected.size > 0 && !selectAllFilter && facts.length > selected.size && (
              <button onClick={() => setSelectAllFilter(true)} data-testid="bulk-select-all-filter" className="rounded border border-slate-300 px-2 py-1 font-medium text-[#1E3D2B]">Sélectionner les {facts.length} résultats du filtre « {filter} »</button>
            )}
            {(selected.size > 0 || selectAllFilter) && (
              <button onClick={() => { setSelected(new Set()); setSelectAllFilter(false); }} data-testid="bulk-clear" className="rounded border border-slate-300 px-2 py-1 text-slate-500">Effacer la sélection</button>
            )}
            <span className="mx-1 h-4 w-px bg-slate-300" />
            <button onClick={exportSelectionCsv} disabled={selCount === 0} data-testid="bulk-export-csv" className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 font-semibold text-slate-600 disabled:opacity-40"><Eye size={13} /> Exporter la sélection (CSV)</button>
            <span className="mx-1 h-4 w-px bg-slate-300" />
            <button onClick={() => setBulkConfirm({ decision: "accepted", scope: selectAllFilter ? "filter" : "selection", count: selCount, label: "Accepter la sélection" })} disabled={busy || !canDecide || selCount === 0} title={!canDecide ? "Rouvrez la revue pour modifier les décisions" : "Accepter"} data-testid="bulk-accept-btn" className="inline-flex items-center gap-1 rounded border border-[#CBE3D1] px-2 py-1 font-semibold text-[#2E7D46] disabled:opacity-40"><CheckCircle2 size={13} /> Accepter</button>
            <button onClick={() => setBulkConfirm({ decision: "rejected", scope: selectAllFilter ? "filter" : "selection", count: selCount, label: "Rejeter la sélection" })} disabled={busy || !canDecide || selCount === 0} title={!canDecide ? "Rouvrez la revue pour modifier les décisions" : "Rejeter"} data-testid="bulk-reject-btn" className="inline-flex items-center gap-1 rounded border border-rose-200 px-2 py-1 font-semibold text-rose-600 disabled:opacity-40"><XCircle size={13} /> Rejeter</button>
            <button onClick={() => setBulkConfirm({ decision: "requires_review", scope: selectAllFilter ? "filter" : "selection", count: selCount, label: "Remettre à revoir" })} disabled={busy || !canDecide || selCount === 0} title={!canDecide ? "Rouvrez la revue pour modifier les décisions" : "Remettre à revoir"} data-testid="bulk-return-btn" className="inline-flex items-center gap-1 rounded border border-amber-200 px-2 py-1 font-semibold text-amber-700 disabled:opacity-40"><RotateCcw size={13} /> Remettre à revoir</button>
            <button onClick={() => setBulkConfirm({ decision: "accepted", scope: "high_clean", count: null, label: "Accepter tous les faits HIGH sans avertissement" })} disabled={busy || !canDecide} title={!canDecide ? "Rouvrez la revue pour modifier les décisions" : ""} data-testid="bulk-accept-high-btn" className="inline-flex items-center gap-1 rounded bg-[#1E3D2B] px-2 py-1 font-semibold text-white disabled:opacity-40"><CheckCircle2 size={13} /> Accepter tous les HIGH sans avertissement</button>
            {!canDecide && <span className="text-[11px] italic text-slate-400" data-testid="bulk-decisions-disabled-note">Rouvrez la revue pour modifier les décisions</span>}
          </div>

        <ScrollableTable className="rounded-xl border border-slate-200" ariaLabel="Faits de la revue — défilement horizontal" testid="review-facts-table">
          <table className="w-full min-w-[900px] text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2"><input type="checkbox" checked={allPageSelected} ref={(el) => { if (el) el.indeterminate = !allPageSelected && pageIds.some((id) => selected.has(id)); }} onChange={togglePage} aria-label="Sélectionner la page visible" data-testid="bulk-select-page" data-no-drag /></th>
                <th className="px-3 py-2">Métrique</th><th className="px-3 py-2">Période</th><th className="px-3 py-2">Valeur</th>
                <th className="px-3 py-2">Unité</th><th className="px-3 py-2">Dimensions</th><th className="px-3 py-2">Conf.</th>
                <th className="px-3 py-2">Avertissements</th><th className="px-3 py-2">Décision</th><th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {facts.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400" data-testid="review-facts-empty">Aucun fait pour ce filtre.</td></tr>}
              {pageFacts.map((f) => (
                <tr key={f.fact_identity} className={`border-b border-slate-100 last:border-0 ${f.blocking ? "bg-orange-50/40" : ""}`} data-testid={`review-fact-${f.fact_identity}`}>
                  <td className="px-3 py-2"><input type="checkbox" checked={selectAllFilter || selected.has(f.fact_identity)} onChange={() => toggleRow(f.fact_identity)} aria-label={`Sélectionner ${f.metric_code}`} data-testid={`bulk-select-${f.fact_identity}`} data-no-drag /></td>
                  <td className="px-3 py-2 font-semibold text-[#1E3D2B]">{f.metric_code}</td>
                  <td className="px-3 py-2 text-slate-500">{f.period?.period_type} {f.period?.period_start}{f.period?.period_end && f.period?.period_end !== f.period?.period_start ? `→${f.period.period_end}` : ""}</td>
                  <td className="px-3 py-2 font-mono text-slate-700" data-testid={`review-fact-value-${f.fact_identity}`}>{f.value != null ? f.value.toLocaleString("fr-FR") : "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{f.unit || "—"}{f.currency ? ` ${f.currency}` : ""}</td>
                  <td className="px-3 py-2 text-slate-500">{Object.entries(f.dimensions || {}).map(([k, v]) => `${k}=${v}`).join(", ") || "—"}</td>
                  <td className="px-3 py-2">{f.mapping_confidence}</td>
                  <td className="px-3 py-2">
                    {(f.warnings || []).length === 0 ? <span className="text-slate-300">—</span> : (
                      <span className="text-amber-700" title={(f.warnings || []).join(", ")}>{(f.warnings || []).slice(0, 2).join(", ")}{f.warnings.length > 2 ? "…" : ""}</span>
                    )}
                    {f.conflict && <span className="ml-1 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">CONFLIT</span>}
                  </td>
                  <td className="px-3 py-2"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${DECISION_STYLE[f.decision]}`}>{f.decision}</span></td>
                  <td className="px-3 py-2">
                    {canDecide && (
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-1">
                          <button onClick={() => decide(f.fact_identity, "accepted")} disabled={busy || f.blocking} title={f.blocking ? "Conflit bloquant — non acceptable" : "Accepter"} data-testid={`review-accept-${f.fact_identity}`} className="rounded border border-[#CBE3D1] px-1.5 py-0.5 text-[#2E7D46] disabled:opacity-30"><CheckCircle2 size={13} /></button>
                          <button onClick={() => decide(f.fact_identity, "rejected")} disabled={busy} data-testid={`review-reject-${f.fact_identity}`} className="rounded border border-rose-200 px-1.5 py-0.5 text-rose-600 disabled:opacity-30"><XCircle size={13} /></button>
                          <button onClick={() => decide(f.fact_identity, "requires_review")} disabled={busy} title="Remettre à revoir" data-testid={`review-return-${f.fact_identity}`} className="rounded border border-amber-200 px-1.5 py-0.5 text-amber-700 disabled:opacity-30"><RotateCcw size={13} /></button>
                        </div>
                        <input value={notes[f.fact_identity] || ""} onChange={(e) => setNotes({ ...notes, [f.fact_identity]: e.target.value })} placeholder="note (facultatif)" data-testid={`review-note-${f.fact_identity}`} className="w-32 rounded border border-slate-200 px-1.5 py-0.5 text-[11px]" />
                      </div>
                    )}
                    {!canDecide && f.review_note && <span className="text-[11px] italic text-slate-400">{f.review_note}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTable>
        <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500" data-testid="review-pagination">
          <span data-testid="review-facts-range">{facts.length === 0 ? "0" : `${curPage * PAGE_SIZE + 1}–${Math.min(facts.length, curPage * PAGE_SIZE + PAGE_SIZE)}`} sur {facts.length} fait(s) — page visible</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={curPage === 0} data-testid="review-page-prev" className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40">Précédent</button>
            <span data-testid="review-page-indicator">Page {curPage + 1} / {pageCount}</span>
            <button onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={curPage >= pageCount - 1} data-testid="review-page-next" className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40">Suivant</button>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-slate-400">Les valeurs normalisées sont en lecture seule — aucune correction numérique n&apos;est possible. La publication ne transmet que des éléments de contrôle (révision attendue) ; le serveur relit les faits acceptés depuis le snapshot immuable.</p>

        {bulkConfirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="bulk-confirm-dialog" onMouseDown={(e) => { if (e.target === e.currentTarget) setBulkConfirm(null); }}>
            <div className="w-full max-w-md rounded-2xl bg-white p-6">
              <h4 className="font-heading text-base font-bold text-[#17201A]">Confirmer la décision groupée</h4>
              <p className="mt-2 text-sm text-slate-600" data-testid="bulk-confirm-message">
                Action : <b>{bulkConfirm.label}</b><br />
                {bulkConfirm.scope === "high_clean"
                  ? <>Tous les faits <b>HIGH sans avertissement</b> (les faits bloqués, en conflit, avec avertissement ou de confiance &lt; HIGH sont exclus automatiquement).</>
                  : <>Nombre de faits concernés : <b data-testid="bulk-confirm-count">{bulkConfirm.count}</b> · Filtre : <b>{filter}</b></>}
                <br />Revue : <b>{review.source?.filename}</b>
              </p>
              <p className="mt-2 text-[11px] text-slate-400">Aucune publication n&apos;est déclenchée. Les datasets ne sont pas modifiés.</p>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setBulkConfirm(null)} data-testid="bulk-confirm-cancel" className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600">Annuler</button>
                <button onClick={() => bulkApply(bulkConfirm)} disabled={busy} data-testid="bulk-confirm-apply" className="rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Confirmer</button>
              </div>
            </div>
          </div>
        )}

        {confirmPublish && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="publish-confirm-dialog">
            <div className="w-full max-w-md rounded-2xl bg-white p-6">
              <h4 className="flex items-center gap-2 font-heading text-base font-bold text-[#17201A]"><AlertTriangle size={18} className="text-amber-600" /> Confirmer la publication</h4>
              <p className="mt-3 text-sm text-slate-600">
                Vous allez publier <b className="text-[#2E7D46]">{review.counts?.accepted}</b> fait(s) accepté(s) dans <b>Lodge Reference Data</b>.
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-500">
                <li>Seuls les faits <b>acceptés</b> sont publiés.</li>
                <li>Les faits <b>rejetés</b>, <b>à revoir</b> et en <b>conflit</b> sont exclus.</li>
                <li>Aucune valeur numérique ne peut être modifiée ici.</li>
              </ul>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setConfirmPublish(false)} disabled={publishing} data-testid="publish-cancel-btn" className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">Annuler</button>
                <button onClick={doPublish} disabled={publishing} data-testid="publish-confirm-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  <Send size={14} /> {publishing ? "Publication…" : "Publier"}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default function AdminTourismData() {
  const [datasets, setDatasets] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [openReviewId, setOpenReviewId] = useState(null);
  const lastTrigger = useRef(null);
  const openIdx = reviews.findIndex((r) => r.review_id === openReviewId);

  const load = async () => {
    try { const { data } = await api.get("/tourism-data/admin/datasets"); setDatasets(data.datasets || []); } catch { /* silent */ }
    try { const { data } = await api.get("/tourism-data/admin/reviews"); setReviews(data.reviews || []); } catch { /* silent */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const toggle = async (d) => {
    const action = d.status === "ready" ? "disable" : "enable";
    try { await api.post(`/tourism-data/admin/datasets/${d.dataset_id}/${action}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="admin-tourism-data">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-[#17201A]"><Leaf size={18} className="text-[#3E7256]" /> Lodge Tourism Data — Revue</h2>
          <p className="mt-1 text-sm text-slate-500">Import → profilage → mapping sémantique → validation → revue Platform Admin → publication explicite vers Lodge Reference Data. La publication est une action explicite depuis une revue terminée.</p>
        </div>
        <button onClick={() => setShowImport(true)} data-testid="tourism-open-import-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#1E3D2B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2C5540]">
          <Upload size={15} /> Importer & créer une revue
        </button>
      </div>

      {/* Reviews */}
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500"><ClipboardCheck size={15} /> Revues d&apos;import</h3>
        <ScrollableTable className="rounded-xl border border-slate-200 bg-white" ariaLabel="Revues d'import — défilement horizontal" testid="tourism-reviews-table">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Source</th><th className="px-4 py-3">Destination</th><th className="px-4 py-3">Faits</th>
                <th className="px-4 py-3">Acceptés</th><th className="px-4 py-3">Conflits</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {reviews.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400" data-testid="tourism-reviews-empty">Aucune revue. Importez une source pour démarrer.</td></tr>}
              {reviews.map((r) => (
                <tr key={r.review_id} className="border-b border-slate-100 last:border-0" data-testid={`tourism-review-${r.review_id}`}>
                  <td className="px-4 py-3 font-semibold text-[#1E3D2B]">{r.source?.filename}</td>
                  <td className="px-4 py-3 text-slate-500">{r.geo?.geo_name || "—"} {r.geo?.geo_level ? `(${r.geo.geo_level})` : ""}</td>
                  <td className="px-4 py-3 font-mono text-slate-700">{r.counts?.total_facts}</td>
                  <td className="px-4 py-3 font-mono text-[#2E7D46]">{r.counts?.accepted}</td>
                  <td className="px-4 py-3 font-mono text-purple-700">{r.counts?.conflict_count}</td>
                  <td className="px-4 py-3"><span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${REVIEW_STATUS[r.status] || STATUS.disabled}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { lastTrigger.current = e.currentTarget; setOpenReviewId(r.review_id); }} data-testid={`tourism-open-review-${r.review_id}`} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-[#1E3D2B]"><ClipboardCheck size={13} /> Ouvrir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTable>
      </div>

      {/* Legacy published datasets (read-only lifecycle) */}
      <div>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">Datasets de référence publiés</h3>
        <ScrollableTable className="rounded-xl border border-slate-200 bg-white" ariaLabel="Datasets publiés — défilement horizontal" testid="tourism-datasets-table">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Destination</th><th className="px-4 py-3">Niveau</th><th className="px-4 py-3">Période</th>
                <th className="px-4 py-3">Métriques</th><th className="px-4 py-3">Faits</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {datasets.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400" data-testid="tourism-empty">Aucun dataset de référence publié.</td></tr>}
              {datasets.map((d) => (
                <tr key={d.dataset_id} className="border-b border-slate-100 last:border-0" data-testid={`tourism-ds-${d.dataset_id}`}>
                  <td className="px-4 py-3 font-semibold text-[#1E3D2B]">{d.geographic_entity}</td>
                  <td className="px-4 py-3 text-slate-500">{d.geographic_level}</td>
                  <td className="px-4 py-3 text-slate-500">{d.period_start} → {d.period_end}</td>
                  <td className="px-4 py-3 text-slate-600">{(d.metrics || []).join(", ")}</td>
                  <td className="px-4 py-3 font-mono text-slate-700">{d.row_count}</td>
                  <td className="px-4 py-3"><span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${STATUS[d.status] || STATUS.disabled}`}>{d.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {d.status !== "loading" && (
                      <button onClick={() => toggle(d)} data-testid={`tourism-toggle-${d.dataset_id}`} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-[#1E3D2B]">
                        {d.status === "ready" ? <><PowerOff size={13} /> Désactiver</> : <><Power size={13} /> Activer</>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTable>
      </div>

      {showImport && <ImportDialog onClose={() => setShowImport(false)} onReviewCreated={(id) => { setShowImport(false); load(); setOpenReviewId(id); }} />}
      {openReviewId && (
        <ReviewDetail
          reviewId={openReviewId}
          onClose={() => { const t = lastTrigger.current; setOpenReviewId(null); load(); requestAnimationFrame(() => { try { t && t.focus(); } catch { /* noop */ } }); }}
          onChanged={load}
          hasPrev={openIdx > 0}
          hasNext={openIdx >= 0 && openIdx < reviews.length - 1}
          onPrev={() => { if (openIdx > 0) setOpenReviewId(reviews[openIdx - 1].review_id); }}
          onNext={() => { if (openIdx >= 0 && openIdx < reviews.length - 1) setOpenReviewId(reviews[openIdx + 1].review_id); }}
        />
      )}
    </div>
  );
}
